import DownloadIcon from '@mui/icons-material/Download'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material'
import { format } from 'date-fns'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { FeatureFlagName } from '../../../api/feature-flags-api'
import { ProposalsApi } from '../../../api/proposals-api'
import { ISSUER_PAGES } from '../../app/app-menu'
import { convertScenarios } from '../../buyer/create-proposal/conversion-utils'
import PayoutMethodInfobox from '../../buyer/create-proposal/payout-method-infobox'
import GicBreadcrumbs, { BreadcrumbItem } from '../../shared/breadcrumbs'
import { FeatureFlagEnabled } from '../../shared/feature-flag-enabled'
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatPercentage,
  formatWamYears,
} from '../../shared/formats'
import { PageMessage } from '../../shared/page-message'
import {
  PaymentRowType,
  ProposalFile,
  ProposalStatus,
  ScheduleType,
} from '../../shared/types'
import { useFeatureFlags } from '../../shared/use-feature-flags'
import AddBidPlaceholder from './add-bid.tsx'
import BidForm from './bid-form'
import PayoutScheduleTable from './payout-schedule-table'
import './proposal-detail-page.scss'
import ReadOnlyBid from './read-only-bid'
import ReadOnlySelectedBid from './read-only-selected-bid'
import ScenarioCard from './scenario-card'
import useBidForms from './use-bid-forms'
import useIssuerProposal from './use-issuer-proposal'

export default function ProposalDetailPage() {
  const { id } = useParams<{ id: string }>() // proposalId from route
  const { proposal, loading, reload } = useIssuerProposal(id)
  const { flags } = useFeatureFlags()

  const bid = useBidForms()

  // Schedule type state for viewing
  const originalScheduleType = useMemo(
    () => proposal?.scheduleType ?? ScheduleType.DEPOSIT,
    [proposal]
  )
  const [viewScheduleType, setViewScheduleType] =
    useState<ScheduleType>(originalScheduleType)

  // Update view schedule type when proposal changes
  useMemo(() => {
    setViewScheduleType(originalScheduleType)
  }, [originalScheduleType])

  // Convert scenarios if view type differs from original
  const displayScenarios = useMemo(() => {
    if (proposal == null) return []
    if (viewScheduleType === originalScheduleType) {
      return proposal.scenarios
    }
    const useDollarAmounts =
      flags[FeatureFlagName.ENABLE_PARTIAL_MATURITIES_BY_DOLLAR_AMOUNT] ?? false
    return convertScenarios(
      proposal.scenarios,
      viewScheduleType,
      useDollarAmounts
    )
  }, [proposal, viewScheduleType, originalScheduleType, flags])

  // Check if any scenario contains a dollar amount payment
  const hasDollarAmount = useMemo(() => {
    if (proposal == null) return false
    return proposal.scenarios.some((scenario) =>
      scenario.paymentSchedule.some(
        (row) => row.inputType === PaymentRowType.DOLLAR
      )
    )
  }, [proposal])

  // Bids can only be edited if proposal is active
  const isEditable = useMemo(() => {
    if (proposal == null) return false
    if (proposal.status !== ProposalStatus.ACTIVE) return false
    return true
  }, [proposal])
  const submitError = bid.submitError

  const extractFileName = (file: ProposalFile) => file.name

  const handleDownload = (file: ProposalFile) => () => {
    void ProposalsApi.downloadFile(file.id, file.name)
  }

  const handleSubmit = async () => {
    if (await bid.submit()) await reload()
  }

  const breadcrumbs: BreadcrumbItem[] = useMemo(() => {
    if (proposal == null)
      return [{ label: 'Proposals', path: ISSUER_PAGES.PROPOSALS.path }]
    const formattedDate =
      proposal.bidDeadline != null && proposal.bidDeadline !== ''
        ? format(new Date(proposal.bidDeadline), 'MM/dd/yyyy')
        : '-'
    return [
      { label: 'Proposals', path: ISSUER_PAGES.PROPOSALS.path },
      {
        label: `${proposal.buyerTenantName} - ${proposal.fund} - ${formattedDate}`,
      },
    ]
  }, [proposal])

  if (loading) {
    return <PageMessage message="Loading..." />
  }

  if (proposal == null) {
    return <PageMessage message="Proposal not found" />
  }

  return (
    <Box className="issuer-proposal-container">
      <Box className="proposal-detail-header">
        <GicBreadcrumbs items={breadcrumbs} />
      </Box>
      <Box className="proposal-detail-content">
        <Box>
          {/* Basic Information */}
          <Typography variant="h5" className="section-title">
            Basic Information
          </Typography>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 2, lg: 2 }}>
              <Typography variant="subtitle2" className="info-card-label">
                Buyer
              </Typography>
              <Typography variant="body1">
                {proposal.buyerTenantName}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
              <Typography variant="subtitle2" className="info-card-label">
                Fund
              </Typography>
              <Typography variant="body1">{proposal.fund}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
              <Typography variant="subtitle2" className="info-card-label">
                Bid Deadline
              </Typography>
              <Typography variant="body1">
                {formatDateTime(proposal.bidDeadline)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2, lg: 2 }}>
              <Typography variant="subtitle2" className="info-card-label">
                Status
              </Typography>
              <Typography variant="body1">{proposal.status}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2, lg: 2 }}>
              <Typography variant="subtitle2" className="info-card-label">
                Deposit Amount
              </Typography>
              <Typography variant="body1">
                {formatCurrency(proposal.depositAmount)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2, lg: 2 }}>
              <Typography variant="subtitle2" className="info-card-label">
                Deposit Date
              </Typography>
              <Typography variant="body1">
                {formatDate(proposal.depositDate)}
              </Typography>
            </Grid>
          </Grid>

          {/* Windsurf generated - Feature flagged description section (read-only for issuers) */}
          <FeatureFlagEnabled flag="ENABLE_PROPOSAL_DESCRIPTIONS">
            <Box sx={{ mt: '2rem' }}>
              <Typography variant="subtitle2" className="info-card-label">
                Notes for Bidders
              </Typography>
              <Typography
                variant="body1"
                className={`description-text-issuer ${proposal.description != null && proposal.description.trim() !== '' ? 'has-content' : 'no-content'}`}
              >
                {proposal.description != null &&
                proposal.description.trim() !== ''
                  ? proposal.description
                  : 'No notes provided'}
              </Typography>
            </Box>
          </FeatureFlagEnabled>
        </Box>

        {proposal.files.length > 0 && (
          <>
            <Divider className="divider-spacing" />
            <Box>
              <Typography variant="h5" className="section-title">
                Attached Files
              </Typography>
              <Card variant="outlined" className="files-card">
                <Table size="small">
                  <TableBody>
                    {proposal.files.map((file) => (
                      <TableRow key={file.id} className="file-row">
                        <TableCell component="th" scope="row">
                          {extractFileName(file)}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={handleDownload(file)}
                          >
                            <DownloadIcon
                              fontSize="small"
                              className="download-icon"
                            />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </Box>
          </>
        )}

        <Divider className="divider-spacing" />

        <Box>
          <Typography variant="h5" className="section-title">
            Scenarios
          </Typography>

          <PayoutMethodInfobox
            scheduleType={viewScheduleType}
            onScheduleTypeChange={setViewScheduleType}
            visible={flags.ENABLE_PARTIAL_MATURITIES}
            showRoundingWarning={false}
            originalScheduleType={originalScheduleType}
            disableToggle={hasDollarAmount}
            disableToggleMessage="A scenario contains a set dollar amount. Toggling between Deposit and Book Value is not available."
          />

          {submitError === 'At least one bid is required.' && (
            <Typography
              color="error"
              variant="body2"
              style={{ marginBottom: '8px' }}
            >
              {submitError}
            </Typography>
          )}
          <Box className="issuer-scenarios-container">
            {displayScenarios.map((sc, idx) => {
              const form = bid.forms[sc.id]
              const errs = bid.errors[sc.id]
              const maxRows = Math.max(
                ...displayScenarios.map((s) => s.paymentSchedule.length)
              )

              return (
                <ScenarioCard key={sc.id} title={`Scenario ${idx + 1}`}>
                  <Box className="scenario-info-box">
                    <Grid container spacing={2} sx={{ mb: 1, pl: 1, pr: 1 }}>
                      <Grid size={{ xs: 6 }}>
                        <Typography
                          variant="subtitle2"
                          className="scenario-detail-label"
                        >
                          Proposed Rate
                        </Typography>
                        <Typography>
                          {formatPercentage(sc.proposedRate)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography
                          variant="subtitle2"
                          className="scenario-detail-label"
                        >
                          WAM (years)
                        </Typography>
                        <Typography>
                          {formatWamYears(sc.wamYears ?? null)}
                        </Typography>
                      </Grid>
                    </Grid>

                    <PayoutScheduleTable scenario={sc} maxRows={maxRows} />
                  </Box>

                  {sc.bids.length > 0
                    ? (() => {
                        // Sort bids by bidTime, most recent first
                        const sortedBids = [...sc.bids].sort((a, b) => {
                          const timeA =
                            a.createdAt != null && a.createdAt !== ''
                              ? new Date(a.createdAt).getTime()
                              : 0
                          const timeB =
                            b.createdAt != null && b.createdAt !== ''
                              ? new Date(b.createdAt).getTime()
                              : 0
                          return timeB - timeA
                        })

                        const [mostRecentBid, ...olderBids] = sortedBids

                        return (
                          <Box>
                            {/* Most recent bid - always visible */}
                            <ReadOnlyBid
                              key={mostRecentBid.id}
                              bid={mostRecentBid}
                            />

                            {/* Older bids in accordion */}
                            {olderBids.length > 0 && (
                              <Accordion className="older-bids-accordion">
                                <AccordionSummary
                                  expandIcon={<ExpandMoreIcon />}
                                  aria-controls="older-bids-content"
                                  id="older-bids-header"
                                >
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    View {olderBids.length} older bid
                                    {olderBids.length !== 1 ? 's' : ''}
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Box className="older-bids-container">
                                    {olderBids.map((bid) => (
                                      <ReadOnlyBid key={bid.id} bid={bid} />
                                    ))}
                                  </Box>
                                </AccordionDetails>
                              </Accordion>
                            )}
                          </Box>
                        )
                      })()
                    : null}

                  {isEditable &&
                    (form != null ? (
                      <BidForm
                        form={form}
                        errors={errs}
                        onFieldChange={(field, val) =>
                          bid.sanitizeChange(sc.id, field, val)
                        }
                        onDelete={() => bid.deleteBid(sc.id)}
                      />
                    ) : (
                      <AddBidPlaceholder onAdd={() => bid.addBid(sc.id)} />
                    ))}

                  {/* Show selected bid when proposal is completed and there are selected bids */}
                  {!isEditable && sc.selectedBids.length > 0 && (
                    <>
                      {sc.selectedBids.map((selectedBid) => (
                        <ReadOnlySelectedBid
                          key={selectedBid.selectedBidId}
                          selectedBid={selectedBid}
                        />
                      ))}
                    </>
                  )}
                </ScenarioCard>
              )
            })}
          </Box>
        </Box>

        {isEditable && (
          <Box className="submit-actions-container">
            {submitError != null && submitError !== '' && (
              <Typography color="error" variant="body2">
                {submitError}
              </Typography>
            )}
            <Button variant="contained" onClick={() => void handleSubmit()}>
              Submit
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  )
}
